import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash-es/debounce';
import intlShape from '@/shared/utils/intlShape';

import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import Tag from '@/shared/components/Tag';
import adminpulse from '@/shared/utils/adminpulse';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import messages from '../relationOverviewMessages';

export default class UpdateRelationTagsModal extends Component {
  state = {
    action: 'add',
    availableTags: [],
    newTag: '',
    selectedTags: [],
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onlyAllowAdd: PropTypes.bool,
  };

  static defaultProps = {
    onlyAllowAdd: false,
  };

  componentDidMount() {
    this.retrieveTags();
  }

  retrieveTags = () => {
    api.base.get(`${config.appUrls.api}/api/relation/tags`).then(({ data }) => {
      this.setState({ availableTags: data.sort(dynamicSort('name')) });
    });
  };

  actionChanged = event => {
    const action = event.target.value;
    this.setState({
      action,
    });
  };

  isFormInvalid = () => {
    const { action, selectedTags } = this.state;
    if (!action || !selectedTags.length) return true;

    return false;
  };

  // debounce makes sure that if this method is called again within 300 ms, only the last call is executed.
  // this method adds the tag automatically to the document when it is found in the list of tags
  // this is needed because if there are two tags: test and test2, and the user starts typing, test2 will never be able
  // to be added to the document because the value that was entered will me immediately processed and found
  debouncedFindAndAddTag = debounce(value => {
    const { availableTags } = this.state;
    if (value && availableTags.find(x => x.name === value)) {
      this.addTag(value);
      this.setState({ newTag: '' });
    }
  }, 400);

  onTagValueChanged = value => {
    this.setState({ newTag: value });
    this.debouncedFindAndAddTag(value);
  };

  addTag = tagName => {
    if (!tagName) return;
    const cleanTagName = adminpulse.removeDuplicateSpaces(tagName);

    const { selectedTags } = this.state;

    if (selectedTags.some(x => x.toLowerCase() === cleanTagName.toLowerCase())) return;
    this.setState({
      selectedTags: [...selectedTags, cleanTagName],
    });
  };

  onSave = () => {
    const { action, selectedTags } = this.state;
    const { onSave } = this.props;

    return onSave({ action, tags: selectedTags });
  };

  closeModal = () => {
    const { onClose } = this.props;

    this.setState(state => ({
      ...state,
      selectedTags: [],
    }));

    onClose();
  };

  onRemoveTag = tag => {
    const { selectedTags } = this.state;

    this.setState({
      selectedTags: selectedTags.filter(x => x !== tag.name),
    });
  };

  render() {
    const { show, intl, onlyAllowAdd } = this.props;
    const { action, availableTags, newTag, selectedTags } = this.state;

    if (!show) return null;

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.AddOrRemoveTags)}
        show={show}
        onClose={this.closeModal}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
      >
        <div className="form-horizontal">
          <div>
            {onlyAllowAdd ? null : (
              <div className="form-group">
                <label className="control-label col-md-3">
                  <FormattedMessage {...messages.Action} />
                </label>
                <div className="col-md-9">
                  <select onChange={this.actionChanged} className="form-control" value={action}>
                    <option value="add">{intl.formatMessage(messages.AddTags)}</option>
                    <option value="remove">{intl.formatMessage(messages.RemoveTags)}</option>
                  </select>
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="control-label col-md-3">
                <FormattedMessage {...messages.Tags} />
              </label>
              <div className="col-md-9">
                <div className="margin20B">
                  <input
                    type="text"
                    maxLength="200"
                    className="form-control tag-input"
                    onBlur={() => {
                      if (newTag !== '') {
                        this.addTag(newTag);
                        this.setState({ newTag: '' });
                      }
                    }}
                    value={newTag}
                    placeholder={intl.formatMessage(messages.EnterTag)}
                    onChange={e => this.onTagValueChanged(e.target.value.replace(';', ''))}
                    list="tags"
                    autoComplete="off"
                    onKeyDown={e => {
                      if (e.keyCode === 13) {
                        this.addTag(newTag);
                        this.setState({ newTag: '' });
                      } else if (e.keyCode === 27) {
                        this.setState({ newTag: '' });
                      }
                    }}
                  />
                </div>
                <div>
                  {selectedTags &&
                    selectedTags.map((item, index) => (
                      <Tag
                        key={`Tag-${index}`}
                        onRemove={this.onRemoveTag}
                        item={{ value: { name: item }, name: item, id: `Tag-${item}` }}
                        color={(availableTags.find(t => t.name === item) || {}).color}
                      />
                    ))}
                </div>
                <datalist id="tags">
                  {availableTags && availableTags.map(t => <option key={t.id} value={t.name} />)}
                </datalist>
              </div>
            </div>
          </div>
        </div>
      </OkCancelModal>
    );
  }
}
